

import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { ToolConfigParam, ToolConfigWebsiteSearch } from '@shared/index';
import { TooltipModule } from 'ngx-bootstrap/tooltip';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { CardModule } from 'primeng/card';
import { DrawerModule } from 'primeng/drawer';
import { IconFieldModule } from 'primeng/iconfield';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { Menu, MenuModule } from 'primeng/menu';
import { MessageModule } from 'primeng/message';
import { TableModule } from "primeng/table";
import { BulkAction, BulkActionBarComponent } from 'src/app/shared/components/bulk-action-bar/bulk-action-bar.component';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TooltipModule as PrimeTooltipModule } from 'primeng/tooltip';
import { SkillIdInputComponent } from 'src/app/shared/components/ai-bot/utils/skill-id-input/skill-id-input.component';
import { SkillUsageListComponent } from 'src/app/shared/components/ai-bot/utils/skill-usage-list/skill-usage-list.component';
import { ShortyPipe } from 'src/app/shared/pipes/shorty.pipe';
import { AdminService } from '@shared-angular/services/admin.service';
import { BotService } from 'src/app/shared/services/bot.service';
import { CounterService } from '@shared-angular/services/counter.service';
import { RouteShareService } from 'src/app/shared/services/route-share.service';
import { ToastrService } from '@shared-angular/services/toastr.service';
import { ManualLinkComponent } from "../../../../../../shared/components/rag-admin/manual-link/manual-link.component";
import { SecretPlaceholderListComponent } from "../../../../../../shared/components/secret-placeholder-list/secret-placeholder-list.component";
import { OnlineSourcesTabDetailComponent } from './online-sources-tab-detail/online-sources-tab-detail.component';
import { LanguageService } from '@shared-angular/services/language.service';

@Component({
  selector: 'app-bot-toolconfig',
  templateUrl: './online-sources-tab.component.html',
  styleUrl: './online-sources-tab.component.scss',
  imports: [SkillIdInputComponent, SkillUsageListComponent, FormsModule, TooltipModule, PrimeTooltipModule, ShortyPipe, ManualLinkComponent, SecretPlaceholderListComponent, MenuModule, ButtonModule, ConfirmDialogModule, TableModule, BulkActionBarComponent, ToggleSwitchModule, MessageModule, CardModule, InputGroupModule, InputGroupAddonModule, IconFieldModule, InputIconModule, InputTextModule, DrawerModule, OnlineSourcesTabDetailComponent],
  providers: [ConfirmationService]
})
export class OnlineSourcesTabComponent implements OnInit {
  public get bulkActions(): BulkAction[] {
    return [
      {
        id: 'bulk-delete',
        label: $localize`:@@bulk-delete:Löschen`,
        icon: 'ri ri-delete-bin-line',
        command: () => this.removeSelectedToolConfigs()
      }
    ];
  }

  public selectedToolConfigs: ToolConfigWebsiteSearch[] = [];

  public get allSelected(): boolean {
    return this.toolConfigs.length > 0 && this.selectedToolConfigs.length === this.toolConfigs.length;
  }

  public editIndex: number = -1;
  public toolDescription: string = '';
  public toolUrl: string = 'https://';
  public toolType = 'websitesearch' as const;
  protected isToolkitAddingInProgress: boolean = false;
  private toolConfigParamList: ToolConfigParam[] = [];

  constructor(
    private router: Router,
    private toastr: ToastrService,
    public counter: CounterService,
    private adminService: AdminService,
    public rss: RouteShareService,
    private botService: BotService,
    private langService: LanguageService,
    private route: ActivatedRoute,
    private confirmationService: ConfirmationService
  ) { }

  public ngOnInit(): void {
    this.route.queryParams.subscribe(params => {
      if (params['open']) {
        this.showDetailDrawer(params['open'] as string);
      }
    });
  }

  public get toolConfigs(): ToolConfigWebsiteSearch[] {
    return this.rss.botConfig.toolConfigs.filter(tc => tc.tool === 'websitesearch' || tc.tool === 'schnittstelle') as ToolConfigWebsiteSearch[];
  }

  // findet heraus ob alle parameter die konfiguriert sind auch in der url vorkommen
  public allParametersInUrl(tc: ToolConfigWebsiteSearch) {
    return tc.params.find((param: ToolConfigParam) => !tc.url.includes(`{${param.key}}`));
  }

  public async addToolConfig() {
    this.isToolkitAddingInProgress = true;
    const toolConfig: ToolConfigWebsiteSearch = {
      description: this.toolDescription.trim(),
      tool: "websitesearch",
      id: '',
      skillId: '',
      enabled: true,
      params: this.toolConfigParamList,
      url: this.toolUrl.trim(),
      name: ''
    };
    toolConfig.name = await this.botService.suggestName(this.rss.botConfig._id, toolConfig.description);
    toolConfig.skillId = await this.botService.suggestSkill(this.rss.botConfig._id, toolConfig.description, toolConfig.name);
    const existingIds = new Set(this.toolConfigs.map(t => t.id));
    this.rss.botConfig.toolConfigs.push(toolConfig);
    await this.rss.saveBotConfigField('toolConfigs');
    this.counter.addTool(this.rss.botConfig._id);
    this.toolDescription = '';
    this.toolUrl = 'https://';
    this.isToolkitAddingInProgress = false;
    this.toolConfigParamList = [];
    const savedTool = this.toolConfigs.find(t => !existingIds.has(t.id));
    if (savedTool) {
      this.showDetailDrawer(savedTool.id);
    }
  }

  protected checkForAdding(): boolean {
    if (this.toolDescription.trim() === '') return true;
    return this.isToolkitAddingInProgress;
  }


  public removeToolConfig(id: string) {
    const index = this.rss.botConfig.toolConfigs.findIndex((tc) => {
      return tc.id === id;
    });
    this.rss.botConfig.toolConfigs.splice(index, 1);
    this.selectedToolConfigs = this.selectedToolConfigs.filter(tool => tool.id !== id);
    void this.rss.saveBotConfigField('toolConfigs');
  }

  public toggleAll(checked: boolean): void {
    this.selectedToolConfigs = checked ? [...this.toolConfigs] : [];
  }

  public removeSelectedToolConfigs(): void {
    this.confirmationService.confirm({
      header: $localize`:@@bulk-delete-skills-header:Ausgewählte Einträge löschen?`,
      icon: 'ri-error-warning-line',
      message: $localize`:@@bulk-delete-skills-message:Möchtest du die ausgewählten Einträge wirklich löschen?`,
      rejectButtonProps: {
        label: $localize`:@@delete-reject:Abbrechen`,
        severity: 'secondary',
        outlined: true
      },
      acceptButtonProps: {
        label: $localize`:@@delete:Löschen`,
        severity: 'danger'
      },
      accept: async () => {
        const selectedIds = new Set(this.selectedToolConfigs.map(tool => tool.id));
        this.rss.botConfig.toolConfigs = this.rss.botConfig.toolConfigs.filter(tool => !selectedIds.has(tool.id));
        this.selectedToolConfigs = [];
        await this.rss.saveBotConfigField('toolConfigs');
      }
    });
  }

  public showMDPreview(toolId: string) {
    const url = this.router.serializeUrl(this.router.createUrlTree(['/' + this.langService.getLanguage() + '/admin/mdpreview', this.rss.botConfig._id, toolId]));
    window.open(url, '_blank');
  }

  public updateToolConfigItem(id: string) {
    const index = this.rss.botConfig.toolConfigs.findIndex((tc) => {
      return tc.id === id;
    });
    const toolIndex = this.toolConfigs.findIndex((tc) => {
      return tc.id === id;
    });
    if (this.toolConfigs[toolIndex] && this.toolConfigs[toolIndex].url.trim() !== '' && this.toolConfigs[toolIndex].description.trim() !== '') {
      this.rss.botConfig.toolConfigs[index] = this.toolConfigs[toolIndex];
      void this.rss.saveBotConfigField('toolConfigs');
    } else {
      this.toastr.error("SAVED", 'Bitte fülle alle Pflichtfelder aus.');
    }
  }

  public saveToolConfig(tool: ToolConfigWebsiteSearch) {
    this.updateToolConfigItem(tool.id);
  }

  public selectedToolId: string = '';
  public detailDrawer: boolean = false;

  public showDetailDrawer(id: string): void {
    this.selectedToolId = id;
    this.detailDrawer = true;
  }

  public toolContextMenu: MenuItem[] = [];

  public openToolMenu(event: Event, menu: Menu, toolConfig: ToolConfigWebsiteSearch) {
    this.toolContextMenu = this.buildToolMenuItems(toolConfig);
    menu.toggle(event);
  }

  private buildToolMenuItems(toolConfig: ToolConfigWebsiteSearch): MenuItem[] {
    const items: MenuItem[] = [];

    items.push({
      label: $localize`:@@edit:Bearbeiten`,
      icon: 'ri ri-pencil-fill',
      command: () => this.showDetailDrawer(toolConfig.id)
    });

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (toolConfig.tool === 'websitesearch') {
      items.push({
        label: $localize`:@@md-preview:Markdown-Vorschau`,
        icon: 'ri ri-eye-line',
        command: () => { this.showMDPreview(toolConfig.id); }
      });
    }

    items.push({
      label: $localize`:@@delete:Löschen`,
      icon: 'ri ri-close-large-fill',
      command: () => { this.removeToolConfig(toolConfig.id); }
    });

    return items;
  }
}
